import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { animate, style, transition, trigger } from "@angular/animations";
import { KeyboardService } from "common/keyboard.service";
import { UtilService } from "common/util.service";

const DEBOUNCE_TIME = 300;

//because of korean 2 char words
const MIN_SEARCH_TERM_LENGTH = 1;

const MAX_SEARCH_TERM_LENGTH = Number.MAX_SAFE_INTEGER;

type CreateAllowedCallback = (args: unknown) => boolean;

@Component({
	selector: "stages-auto-complete",
	templateUrl: "auto-complete.component.html",
	styleUrls: ["./auto-complete.component.scss"],
	animations: [
		trigger("slide", [
			transition(":enter", [
				style({ transform: "translateX({{enterFrom}}%)" }),
				animate("0.4s 0s cubic-bezier(0.4, 0, 0.2, 1)", style({ transform: "translateX({{enterTo}}%)" })),
			]),
			transition(":leave", [
				style({ transform: "translateX({{leaveFrom}}%)" }),
				animate("0.4s 0s cubic-bezier(0.4, 0, 0.2, 1)", style({ transform: "translateX({{leaveTo}}%)" })),
			]),
		]),
	],
})
export class AutoCompleteComponent<T> implements OnInit, OnDestroy, AfterViewInit {
	readonly BACK_BUTTON_INDEX = -1;

	@Input()
	browseButtonActive: boolean = false;

	@Input()
	backButtonActive: boolean = true;

	@Input()
	createDependendElementAllowedCallback?: CreateAllowedCallback;

	@Input()
	dropDownClass?: string;

	@Input()
	focusOnInit: boolean = false;

	@Input()
	inputIconClasses?: string[];

	@Input()
	isDense: boolean = false;

	@Input()
	isLoading: boolean = false;

	@Input()
	minTermLength: number = MIN_SEARCH_TERM_LENGTH;

	@Input()
	maxTermLength = MAX_SEARCH_TERM_LENGTH;

	@Input()
	placeholder: string = "";

	@Input()
	results: T[] = [];

	@Input()
	subordinatedResults?: T[];

	@Input()
	searchEngineAvailable: boolean = true;

	@Input()
	searchResultSeparatorTemplate?: TemplateRef<unknown>;

	@Input()
	searchResultTemplate?: TemplateRef<unknown>;

	@Input()
	term: string = "";

	@Output()
	readonly backButtonPressed = new EventEmitter<void>();

	@Output()
	readonly cancelled = new EventEmitter<void>();

	@Output()
	readonly closeButtonPressed = new EventEmitter<void>();

	@Output()
	readonly escapeKeyPressed = new EventEmitter<void>();

	@Output()
	readonly focusLost = new EventEmitter<T>();

	@Output()
	readonly itemSelected = new EventEmitter<T>();

	@Output()
	readonly searchInput = new EventEmitter<string>();

	@Output()
	readonly openBrowserButtonPressed = new EventEmitter<void>();

	@Output()
	readonly submitted = new EventEmitter<T>();

	@ViewChild("autoCompleteInput", { read: ElementRef, static: true })
	input!: ElementRef;

	animationParams = {
		enterFrom: "0",
		enterTo: "0",
		leaveFrom: "0",
		leaveTo: "0",
	};

	private destroy$ = new Subject<boolean>();

	dropDownOpen: boolean = false;

	index: number = -1;

	isIE: boolean = false;

	mouseDown: boolean = false;

	constructor(
		private keyboardService: KeyboardService,
		private changeDetector: ChangeDetectorRef,
		utilService: UtilService,
	) {
		this.isIE = utilService.browserIsIE();
	}

	ngOnInit(): void {
		const allKeys = fromEvent<KeyboardEvent>(this.input.nativeElement, "keyup");

		allKeys
			.pipe(
				filter((e) => !this.isSpecialKey(e)),
				debounceTime(DEBOUNCE_TIME),
				takeUntil(this.destroy$),
			)
			.subscribe(() => this.triggerOnSearchInput());

		allKeys
			.pipe(
				filter((e) => this.isSpecialKey(e)),
				takeUntil(this.destroy$),
			)
			.subscribe((e) => this.onSpecialKey(e));
	}

	ngAfterViewInit(): void {
		if (this.focusOnInit) {
			this.focus();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
	}

	focus(): void {
		this.input.nativeElement.focus();
	}

	@HostListener("document:click", ["$event.target"])
	onDocumentClick(targetElement: Element): void {
		if (targetElement !== this.input.nativeElement) {
			this.dropDownOpen =
				(this.term && this.term.length > MIN_SEARCH_TERM_LENGTH) || this.isInSubordinatedElementsMode();
		}
	}

	triggerOnSearchInput(): void {
		if (this.isInSubordinatedElementsMode() || (this.term && this.term.length >= MIN_SEARCH_TERM_LENGTH)) {
			this.dropDownOpen = true;
			this.searchInput.emit(this.term);
		} else {
			this.dropDownOpen = false;
		}
	}

	onSpecialKey(event: KeyboardEvent): void {
		if (this.keyboardService.isEscapeKeyPressed(event)) {
			this.triggerEscapeKeyPressed();
		} else if (this.keyboardService.isReturnKeyPressed(event)) {
			this.returnKeyPressed();
		} else if (this.keyboardService.isNavigationKey(event)) {
			if (this.keyboardService.isDownKeyPressed(event)) {
				this.downKeyPressed();
			} else if (this.keyboardService.isUpKeyPressed(event)) {
				this.upKeyPressed();
			} else if (this.keyboardService.isRightKeyPressed(event)) {
				this.rightKeyPressed();
			} else if (this.keyboardService.isLeftKeyPressed(event)) {
				this.leftKeyPressed();
			}
		}
	}

	private triggerEscapeKeyPressed(): void {
		if (this.isInSubordinatedElementsMode()) {
			this.back();
		} else {
			this.resetAnimationParams();
		}
		this.escapeKeyPressed.emit();
	}

	private downKeyPressed(): void {
		this.index++;
		if (
			this.isInSubordinatedElementsMode()
				? this.index > this.subordinatedResults!.length - 1
				: this.index > this.results.length - 1
		) {
			this.index = this.isInSubordinatedElementsMode() && this.backButtonActive ? this.BACK_BUTTON_INDEX : 0;
		}
	}

	private upKeyPressed(): void {
		this.index--;
		if (
			(this.isInSubordinatedElementsMode() && this.backButtonActive && this.index < this.BACK_BUTTON_INDEX) ||
			(this.isInSubordinatedElementsMode() && !this.backButtonActive && this.index < 0) ||
			(!this.isInSubordinatedElementsMode() && this.index < 0)
		) {
			this.index = this.isInSubordinatedElementsMode() ? this.subordinatedResults!.length - 1 : this.results.length - 1;
		}
	}

	isInSubordinatedElementsMode(): boolean {
		return this.subordinatedResults !== undefined;
	}

	private returnKeyPressed(): void {
		if (this.index >= 0 && this.currentIndexExistsInResults()) {
			this.onSelect(
				this.isInSubordinatedElementsMode() ? this.subordinatedResults![this.index] : this.results[this.index],
			);
		} else if (this.index === this.BACK_BUTTON_INDEX && this.backButtonActive) {
			this.back();
		}
	}

	private rightKeyPressed(): void {
		// eslint-disable-next-line  @typescript-eslint/no-explicit-any -- it is just an check for existence
		if (this.index >= 0 && (this.results[this.index] as any).dependentElementsContainer) {
			this.onSelect(this.results[this.index]);
		}
	}

	private currentIndexExistsInResults(): boolean {
		return this.isInSubordinatedElementsMode()
			? this.subordinatedResults!.length > this.index
			: this.results.length > this.index;
	}

	private leftKeyPressed(): void {
		if (this.isInSubordinatedElementsMode() && this.backButtonActive) {
			this.back();
		}
	}

	onSelect(result: T): void {
		this.animationParams = {
			enterFrom: "0",
			enterTo: "-100",
			leaveFrom: "0",
			leaveTo: "-100",
		};
		this.index = -1;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- just a check for existence of property
		if (!(result as any).dependentElementsContainer) {
			this.dropDownOpen = false;
			this.resetSubordinatedElementsMode();
		}
		this.itemSelected.emit(result);
		this.focus();
	}

	onMouseDown(): void {
		this.mouseDown = true;
	}

	onBlur(element: FocusEvent): void {
		if (this.browseButtonActive && this.isIE) {
			this.mouseDown = false;
			return;
		}
		if (!this.mouseDown && !(element.relatedTarget instanceof Element && element.relatedTarget.id === "open-browser")) {
			this.focusLost.emit();
		}
		this.mouseDown = false;
	}

	hasNoResults(): boolean {
		return this.isLoading
			? false
			: this.isInSubordinatedElementsMode()
			? this.subordinatedResults!.length === 0
			: this.results.length === 0;
	}

	isSelected(index: number): boolean {
		return index === this.index;
	}

	closeSearch(): void {
		this.resetAnimationParams();
		this.closeButtonPressed.emit();
	}

	openBrowser(): void {
		this.openBrowserButtonPressed.emit();
	}

	back(): void {
		this.animationParams = {
			enterFrom: "-200",
			enterTo: "-100",
			leaveFrom: "0",
			leaveTo: "100",
		};
		this.changeDetector.detectChanges();
		this.backButtonPressed.emit();
	}

	isSpecialKey(event: KeyboardEvent): boolean {
		return (
			this.keyboardService.isEscapeKeyPressed(event) ||
			this.keyboardService.isReturnKeyPressed(event) ||
			this.keyboardService.isNavigationKey(event)
		);
	}

	showListItem(item: unknown, isSubordinated: boolean): boolean {
		if (isSubordinated && this.createDependendElementAllowedCallback) {
			return this.createDependendElementAllowedCallback(item);
		}
		return true;
	}

	private resetSubordinatedElementsMode(): void {
		this.subordinatedResults = undefined;
	}

	resetAnimationParams(): void {
		console.log("HAPPENED");
		this.animationParams = {
			enterFrom: "0",
			enterTo: "0",
			leaveFrom: "0",
			leaveTo: "0",
		};
	}
}
